import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import {
    AccountingBaseComponent,
    AccountingBaseDisplayType,
    AccountingChange,
    AccountingSelectionInput,
    AccountingSelectionModule,
    IAccountingBulkChangeSelection,
    IAccountingChangeDialogComponentData,
    IAccountingOption,
} from '@lib/accounting';
import { ClassificationSelectionComponent, IClassificationCode } from '@lib/classification';
import { ISiteChange, SiteSelectionComponent } from '@lib/customer-site';
import {
    PurchasingGroup,
    PurchasingGroupDataService,
    ResponsiblePurchasingGroup,
} from '@lib/purchasing-group';
import { MaterialGroupSelectionComponent } from '@lib/material-group';
import { AccountingValidationActivityService } from '@lib/util';
import { IimSpinnerModule } from '@iim-angular/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { Subject, catchError, of, switchMap } from 'rxjs';

// Bridge back-to-back accounting validations so the loading overlay does not flicker off and on
// between a field change and the validation it triggers (EPROC-11028).
const ACCOUNTING_RELOAD_SETTLE_MS = 300;

/**
 * EPROC-11017: unified bulk accounting dialog used by all documents (basket, order, demand, invoice).
 *
 * It always offers the accounting selection (cost center, GL account, project, custom accountings) and
 * additionally the classification and site (Standort) fields. The classification and site fields can be
 * disabled per call site via {@link IAccountingChangeDialogComponentData.hideClassification} and
 * {@link IAccountingChangeDialogComponentData.hideSite}; the site field additionally hides itself when the
 * customer site feature is unavailable (handled inside `lib-site-selection`).
 *
 * Lives in this higher-level lib (not in `@lib/accounting`) because it depends on classification and
 * customer-site; it is opened indirectly from the low-level `AccountingSelectionService` via the
 * `BULK_ACCOUNTING_DIALOG_COMPONENT` token registered in the application module.
 */
@Component({
    selector: 'lib-bulk-accounting-dialog',
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        MatButtonModule,
        MatSlideToggleModule,
        AccountingSelectionModule,
        ClassificationSelectionComponent,
        SiteSelectionComponent,
        AccountingBaseComponent,
        MaterialGroupSelectionComponent,
        IimSpinnerModule,
    ],
    templateUrl: './bulk-accounting-dialog.component.html',
    styleUrl: './bulk-accounting-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkAccountingDialogComponent implements OnDestroy {
    private readonly dialogRef = inject<MatDialogRef<BulkAccountingDialogComponent>>(MatDialogRef);
    public readonly options = inject<IAccountingChangeDialogComponentData>(MAT_DIALOG_DATA);
    private readonly validationActivity = inject(AccountingValidationActivityService);
    private readonly purchasingGroupDataService = inject(PurchasingGroupDataService);

    private readonly values = new Map<string, AccountingChange>();
    public input: AccountingSelectionInput = new AccountingSelectionInput();
    public overwriteExisting = false;

    protected readonly purchasingGroupLabel = marker('Einkaufsgruppe');
    // EPROC-11622: makes explicit that only what the user filled in is written — an empty field never
    // clears an existing value on a position. Shown in every document that uses this dialog.
    protected readonly scopeHint = marker('Nur ausgewählte Verbuchungen und Felder werden gesetzt.');
    protected readonly purchasingGroupDerivedHint = marker('Wird aus der Warengruppe abgeleitet.');
    protected readonly purchasingGroupUnassignedHint = marker('Keine Zuständigkeit hinterlegt.');
    protected readonly purchasingGroupPreviewDisplay = AccountingBaseDisplayType.KEY_AND_NAME;

    private readonly accountingCount = signal(0);
    private readonly classificationCodeId = signal<string | undefined>(undefined);
    private readonly siteCode = signal<string | null>(null);
    private readonly purchasingGroupCode = signal<string | undefined>(undefined);
    // EPROC-11412: selectable active purchasing groups for the bulk field, loaded lazily when shown.
    protected readonly purchasingGroupOptions = signal<readonly IAccountingOption[]>([]);
    protected readonly loadingPurchasingGroups = signal(false);
    private readonly materialGroupCode = signal<string | undefined>(undefined);

    // EPROC-11622: read-only preview of the purchasing group the selected commodity group leads to.
    // Used by documents that derive the purchasing group instead of storing it (basket, UC-EKG-12), so
    // the user sees which group will apply before confirming the dialog.
    protected readonly purchasingGroupPreview = signal<ResponsiblePurchasingGroup | null>(null);
    protected readonly loadingPurchasingGroupPreview = signal(false);
    private readonly purchasingGroupPreviewRequests = new Subject<IPurchasingGroupPreviewRequest>();

    /**
     * The derived purchasing group as "code – name", or null while nothing is derivable yet. Null is
     * rendered as a hint, so the user can tell "not chosen yet" apart from "no responsibility assigned".
     */
    protected readonly purchasingGroupPreviewLabel = computed(() => {
        const preview = this.purchasingGroupPreview();
        if (!preview?.assigned) {
            return null;
        }
        return preview.name ? `${preview.code} – ${preview.name}` : preview.code;
    });

    protected readonly purchasingGroupPreviewOptions = computed<readonly IAccountingOption[]>(() => {
        const preview = this.purchasingGroupPreview();
        if (!preview?.assigned || !preview.code) {
            return [];
        }

        return [{ code: preview.code, name: preview.name ?? '' }];
    });

    protected readonly purchasingGroupPreviewHint = computed(() =>
        this.materialGroupCode() ? this.purchasingGroupUnassignedHint : this.purchasingGroupDerivedHint,
    );

    protected readonly hasSelection = computed(
        () =>
            this.accountingCount() > 0 ||
            !!this.classificationCodeId() ||
            !!this.siteCode() ||
            !!this.purchasingGroupCode() ||
            !!this.materialGroupCode(),
    );

    /**
     * Hides the accounting selection behind a loading animation while a validation is in flight and
     * reveals it again only once the result has settled, so the per-field required markers/asterisks
     * appear at once with their final state instead of flickering in one after another — the same
     * coordination the basket already applies on a company-code change (EPROC-11028).
     */
    protected readonly accountingReloading = signal(false);
    private settleTimer?: ReturnType<typeof setTimeout>;

    constructor() {
        effect(() => {
            if (this.validationActivity.isValidating()) {
                this.clearSettleTimer();
                this.accountingReloading.set(true);
            } else {
                this.scheduleSettle();
            }
        });

        // EPROC-11412: load the active purchasing groups only when the field is actually shown. Inactive
        // groups are filtered out here just like the per-position dropdown, so the bulk field can never
        // offer a group that the single-item endpoint would reject.
        if (this.options.showPurchasingGroup) {
            this.loadingPurchasingGroups.set(true);
            this.purchasingGroupDataService
                .getSelectable()
                .pipe(takeUntilDestroyed())
                .subscribe({
                    next: (groups) => {
                        this.purchasingGroupOptions.set(
                            groups.filter((group) => group.active).map(toPurchasingGroupOption),
                        );
                        this.loadingPurchasingGroups.set(false);
                    },
                    error: () => this.loadingPurchasingGroups.set(false),
                });
        }

        // switchMap so a quick second commodity group selection cannot be overtaken by the first
        // response — the preview must always describe the group that is currently selected.
        this.purchasingGroupPreviewRequests
            .pipe(
                switchMap((request) =>
                    this.purchasingGroupDataService
                        .getResponsible(request.companyCodeId, request.materialGroupCode)
                        .pipe(catchError(() => of(null))),
                ),
                takeUntilDestroyed(),
            )
            .subscribe((responsiblePurchasingGroup) => {
                this.purchasingGroupPreview.set(responsiblePurchasingGroup);
                this.loadingPurchasingGroupPreview.set(false);
            });
    }

    public ngOnDestroy(): void {
        this.clearSettleTimer();
    }

    public accountingChanged(event: AccountingChange): void {
        if (event.accountAssignmentId) {
            this.values.set(event.identifier, event);
        } else {
            this.values.delete(event.identifier);
        }
        this.accountingCount.set(this.values.size);
        this.input = this.input.applyEvent(event);
    }

    public classificationChanged(event: IClassificationCode | undefined): void {
        this.classificationCodeId.set(event?.irdicc);
    }

    public siteChanged(change: ISiteChange | null): void {
        this.siteCode.set(change ? change.siteCode : null);
    }

    public purchasingGroupSelected(option: IAccountingOption): void {
        this.purchasingGroupCode.set(option.code || undefined);
    }

    public purchasingGroupCleared(): void {
        this.purchasingGroupCode.set(undefined);
    }

    public materialGroupChanged(materialGroupCode: string | undefined): void {
        const selectedMaterialGroupCode = materialGroupCode || undefined;
        this.materialGroupCode.set(selectedMaterialGroupCode);
        this.refreshPurchasingGroupPreview(selectedMaterialGroupCode);
    }

    public select(): void {
        const result: IAccountingBulkChangeSelection = {
            changes: Array.from(this.values.values()),
            overwriteExisting: this.overwriteExisting,
            classificationCodeId: this.classificationCodeId(),
            siteCode: this.siteCode() ?? undefined,
            purchasingGroupCode: this.purchasingGroupCode(),
            materialGroupCode: this.materialGroupCode(),
        };
        this.dialogRef.close(result);
    }

    public cancel(): void {
        this.dialogRef.close();
    }

    /**
     * Without a commodity group — or without the company code the responsibility is scoped to — there is
     * nothing to derive, so the preview is cleared instead of asking the backend.
     */
    private refreshPurchasingGroupPreview(materialGroupCode: string | undefined): void {
        if (!this.options.showPurchasingGroupPreview) {
            return;
        }

        const companyCodeId = this.options.companyCodeId;
        if (!materialGroupCode || !companyCodeId) {
            this.purchasingGroupPreview.set(null);
            this.loadingPurchasingGroupPreview.set(false);
            return;
        }

        this.loadingPurchasingGroupPreview.set(true);
        this.purchasingGroupPreviewRequests.next({ companyCodeId, materialGroupCode });
    }

    private scheduleSettle(): void {
        this.clearSettleTimer();
        this.settleTimer = setTimeout(() => {
            this.settleTimer = undefined;
            if (this.validationActivity.isValidating()) {
                return;
            }
            this.accountingReloading.set(false);
        }, ACCOUNTING_RELOAD_SETTLE_MS);
    }

    private clearSettleTimer(): void {
        if (this.settleTimer) {
            clearTimeout(this.settleTimer);
            this.settleTimer = undefined;
        }
    }
}

/** EPROC-11622: the pair the responsibility lookup is scoped to, carried through the request stream. */
interface IPurchasingGroupPreviewRequest {
    readonly companyCodeId: string;
    readonly materialGroupCode: string;
}

// EPROC-11412: map a purchasing group onto the generic accounting option shape used by app-accounting-base.
function toPurchasingGroupOption(purchasingGroup: PurchasingGroup): IAccountingOption {
    return {
        code: purchasingGroup.code,
        name: purchasingGroup.name,
    };
}
